import React, { useState } from "react";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Link2, ExternalLink, X, Plus, FileText } from "lucide-react";
import { LinkDocDialog, LinkProviderBadge } from "../shared/LinkDocDialog";
import { fmtDate, Pill } from "./constants";

export const PRINTER_DOC_TYPES = [
    { value: "invoice", label: "Facture" },
    { value: "contract", label: "Contrat / maintenance" },
    { value: "warranty", label: "Garantie" },
    { value: "manual", label: "Manuel / fiche technique" },
    { value: "photo", label: "Photo" },
    { value: "other", label: "Autre" },
];
const TYPE_COLORS = { invoice: "bg-emerald-50 text-emerald-700", contract: "bg-orange-50 text-orange-700", warranty: "bg-blue-50 text-blue-700", manual: "bg-gray-100 text-gray-600", photo: "bg-pink-50 text-pink-700", other: "bg-gray-100 text-gray-600" };
const typeLabel = (v) => PRINTER_DOC_TYPES.find((t) => t.value === v)?.label || "Autre";

// Liste de documents par lien (OneDrive / SharePoint…) pour une imprimante ou une entreprise.
export function PrinterDocuments({ documents = [], onAdd, onChanged, compact = false, testId = "printer-documents" }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const add = async ({ url, name, type, date }) => {
        setSaving(true);
        try {
            await onAdd({ url, name, doc_type: type, doc_date: date });
            toast.success("Lien ajouté");
            setOpen(false);
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    const remove = async (d) => {
        if (!window.confirm(`Supprimer le lien "${d.name}" ?`)) return;
        try { await api.deletePrinterDocument(d.id); toast.success("Lien supprimé"); onChanged?.(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    return (
        <div data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center gap-1.5 font-semibold text-gray-800 ${compact ? "text-[11px]" : "text-sm"}`}>
                    <FileText className={`${compact ? "w-3 h-3" : "w-4 h-4"} text-gray-400`} />Documents & liens{documents.length > 0 && ` (${documents.length})`}
                </span>
                <Button variant="outline" size="sm" className={`${compact ? "h-6 px-2 text-[10px]" : "h-7 text-xs"} text-sky-700 border-sky-200 hover:bg-sky-50`} onClick={() => setOpen(true)} data-testid={`${testId}-add-btn`}>
                    <Plus className="w-3 h-3 mr-1" />Lien
                </Button>
            </div>
            {documents.length === 0 ? (
                <p className={`${compact ? "text-[10px]" : "text-xs"} text-gray-400 ${compact ? "py-1" : "py-3 text-center"}`}>Aucun document. Ajoutez un lien OneDrive / SharePoint (facture, contrat, manuel…).</p>
            ) : (
                <div className="space-y-1">
                    {documents.map((d) => (
                        <div key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-gray-50 group" data-testid={`pdoc-row-${d.id}`}>
                            <Link2 className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                            <a href={d.url} target="_blank" rel="noreferrer noopener" className="text-xs text-gray-700 hover:text-teal-600 hover:underline truncate flex-1 min-w-0" title={d.url} data-testid={`pdoc-link-${d.id}`}>{d.name}</a>
                            <Pill className={TYPE_COLORS[d.doc_type] || TYPE_COLORS.other}>{typeLabel(d.doc_type)}</Pill>
                            {!compact && d.doc_date && <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtDate(d.doc_date)}</span>}
                            {!compact && <LinkProviderBadge url={d.url} />}
                            <a href={d.url} target="_blank" rel="noreferrer noopener" className="text-gray-400 hover:text-sky-600" title="Ouvrir"><ExternalLink className="w-3 h-3" /></a>
                            <button onClick={() => remove(d)} className="text-gray-400 hover:text-red-500" title="Supprimer" data-testid={`pdoc-delete-${d.id}`}><X className="w-3 h-3" /></button>
                        </div>
                    ))}
                </div>
            )}
            <LinkDocDialog open={open} onClose={() => setOpen(false)} onSubmit={add} submitting={saving} typeOptions={PRINTER_DOC_TYPES} defaultType="invoice" showDate dateLabel="Date (facture, contrat…)" />
        </div>
    );
}
